import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, EmptyState } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { Inbox, Send, MessageSquare, ThumbsUp, ThumbsDown, Clock, Ban, RotateCcw, UserCheck } from "lucide-react";

const DECISIONS = [
  { key: "positive", label: "Positive", icon: ThumbsUp, tone: "bg-emerald-600 hover:bg-emerald-700" },
  { key: "follow_up", label: "Follow-Up", icon: Clock, tone: "bg-sky-600 hover:bg-sky-700" },
  { key: "future_reference", label: "Future Ref.", icon: RotateCcw, tone: "bg-slate-600 hover:bg-slate-700" },
  { key: "negative", label: "Negative", icon: ThumbsDown, tone: "bg-orange-600 hover:bg-orange-700" },
  { key: "do_not_contact", label: "Do Not Contact", icon: Ban, tone: "bg-red-600 hover:bg-red-700" },
];

export default function RAReview() {
  const { can } = useAuth();
  const canAssign = can("ra:assign_bdm");
  const [items, setItems] = useState(null);
  const [detail, setDetail] = useState(null);
  const [notesInternal, setNotesInternal] = useState("");
  const [notesBdm, setNotesBdm] = useState("");
  const [returnReason, setReturnReason] = useState("");
  const [bdms, setBdms] = useState([]);
  const [bdmId, setBdmId] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignLead, setAssignLead] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/ra/review-queue"); setItems(data.items); }
    catch (e) { toast.error(apiErr(e)); setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (canAssign) api.get("/ra/bdms").then(({ data }) => setBdms(data.items)).catch(() => {}); }, [canAssign]);

  const openDetail = async (id) => {
    try { const { data } = await api.get(`/ra/leads/${id}`); setDetail(data); setNotesInternal(""); setNotesBdm(""); setReturnReason(""); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const decide = async (decision) => {
    const id = detail.lead.id;
    if (decision === "returned" && !returnReason.trim()) { toast.error("A reason is required to return"); return; }
    try {
      await api.post(`/ra/leads/${id}/review`, { decision, notes_internal: notesInternal, notes_for_bdm: notesBdm, return_reason: returnReason });
      toast.success(`Marked ${decision.replace(/_/g, " ")}`);
      if (decision === "positive" && canAssign) { setAssignLead({ ...detail.lead, admin_notes_for_bdm: notesBdm }); setDetail(null); setAssignOpen(true); }
      else setDetail(null);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const assign = async () => {
    if (!bdmId) { toast.error("Select a BDM"); return; }
    try { await api.post(`/ra/leads/${assignLead.id}/assign-bdm`, { bdm_user_id: bdmId }); toast.success("Handed off to BDM"); setAssignOpen(false); setBdmId(""); setAssignLead(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <PageHeader title="Admin Review Queue" subtitle="Review submitted research leads and route positive leads to a BDM" />

      {items === null ? <Skeleton className="h-40" /> : items.length === 0 ? (
        <EmptyState icon={Inbox} title="Nothing to review" description="Submitted research leads will appear here." />
      ) : (
        <Card className="overflow-hidden" data-testid="ra-review-queue">
          <div className="divide-y divide-border">
            {items.map((l) => (
              <div key={l.id} className="flex items-center gap-3 p-3.5 cursor-pointer hover:bg-accent/40" data-testid={`ra-review-${l.id}`} onClick={() => openDetail(l.id)}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{l.company}</div>
                  <div className="text-xs text-muted-foreground">{l.position_title} · {l.location || "—"}{l.assigned_ra_name ? ` · R.A.: ${l.assigned_ra_name}` : ""}</div>
                </div>
                <div className="hidden sm:flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Send className="h-3.5 w-3.5" />{l.outreach_count || 0}</span>
                  <span className="flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" />{l.reply_count || 0}</span>
                </div>
                <div className="text-xs text-muted-foreground w-24 text-right">{fmtDate(l.submitted_at)}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Review dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle>{detail.lead.company}</DialogTitle>
                <DialogDescription>{detail.lead.position_title} · {detail.lead.location || "—"}{detail.lead.assigned_ra_name ? ` · Sourced by ${detail.lead.assigned_ra_name}` : ""}</DialogDescription>
              </DialogHeader>
              <div className="space-y-2 max-h-48 overflow-auto">
                {detail.outreach.map((o) => <div key={o.id} className="text-sm border-l-2 border-sky-400 pl-3"><b>{o.subject}</b><div className="text-muted-foreground text-xs">{o.body}</div></div>)}
                {detail.replies.map((r) => <div key={r.id} className="text-sm border-l-2 border-violet-400 pl-3"><b>{r.subject}</b><div className="text-muted-foreground text-xs">{r.body}</div></div>)}
              </div>
              <Textarea placeholder="Internal notes (Admin/Super Admin only)" value={notesInternal} onChange={(e) => setNotesInternal(e.target.value)} data-testid="review-notes-internal" />
              <Textarea placeholder="Notes for BDM (shared on handoff — no R.A. identity)" value={notesBdm} onChange={(e) => setNotesBdm(e.target.value)} data-testid="review-notes-bdm" />
              <Textarea placeholder="Return reason (required only when returning for correction)" value={returnReason} onChange={(e) => setReturnReason(e.target.value)} data-testid="review-return-reason" />
              <DialogFooter className="flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => decide("returned")} data-testid="review-return" className="gap-1.5"><RotateCcw className="h-4 w-4" /> Return</Button>
                {DECISIONS.map((d) => (
                  <Button key={d.key} size="sm" onClick={() => decide(d.key)} data-testid={`review-${d.key}`} className={`gap-1.5 text-white ${d.tone}`}>
                    <d.icon className="h-4 w-4" /> {d.label}
                  </Button>
                ))}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign BDM dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign to BDM</DialogTitle><DialogDescription>Creates the Client, Contact and one Sales Pipeline opportunity for the selected BDM. The R.A. identity is never shared.</DialogDescription></DialogHeader>
          <Select value={bdmId} onValueChange={setBdmId}>
            <SelectTrigger data-testid="assign-bdm-select"><SelectValue placeholder="Select a BDM" /></SelectTrigger>
            <SelectContent>{bdms.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}{b.region ? ` · ${b.region}` : ""}</SelectItem>)}</SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)} data-testid="assign-bdm-cancel">Cancel</Button>
            <Button onClick={assign} data-testid="assign-bdm-confirm" className="gap-1.5 bg-[#255058] hover:bg-[#1c3d43] text-white"><UserCheck className="h-4 w-4" /> Confirm Handoff</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
